import { useEffect, useRef, useState } from "react";
import { AssessmentShell } from "../components/AssessmentShell";
import { useCustomSpeechToText } from "../hooks/useCustomSpeechToText";
import { useNavigate } from "react-router";
import { LTitle } from "../../../common/components/LTitle";
import { useApi } from "../../../services/useApi";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { Word, WordResponseType } from "../../../common/store/assessmentSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { Colors } from "../../../styles/colors";
import { sleepFor, useCountDown } from "../../../common/helpers/countHelper";
import { ResultType } from "../../../utils/stt/useSpeechToText";
import { updateCurrentFlow } from "../../../common/store/assessmentFlowSlice";
import { NavItems } from "../../../common/helpers/navigation";
import { translate } from "react-i18nify";
import { intersection } from "lodash";
import { Loader } from "@components";
import { LCountDown } from "@components/LCountDown";
import { LProgressCircle } from "@components/LProgressCircle";
import { sanitizeResponse } from "@helpers/stringHelper";
import { playAudioWithRetryAsync } from "@fassessment/hooks/useAudio";
import { useScreenWakeLock } from "@fassessment/hooks/useScreenWakeLock";
import { AssessmentTimeout } from "@fassessment/components/AssessmentTimeout";
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { v4 as uuidv4 } from "uuid";
import { SttSourceType } from "@store/generalTypes";
import { trackDiagnosticException, trackDiagnosticTrace } from "@utils/diagnostics/appInsightsDiagnostics";

const getNextElement = (element: Word, wordList: Word[]): Word | undefined => wordList[wordList.indexOf(element) + 1];

const getAcceptedWords = (word: Word): string[] => [word.value, ...word.alternates];
const PRESENT_TARGETS_DIAGNOSTIC_AREA = "present-targets";
const PRESENT_TARGETS_DIAGNOSTIC_EVENTS = new Set([
    "countdown:done",
    "start:failed-return-to-countdown",
    "start:media-recovery-auto-reset-start",
    "start:media-recovery-auto-reset-complete",
    "start:media-recovery-retry-success",
    "start:media-recovery-retry-failed",
    "start:media-recovery-manual-required",
    "silent-recovery:exhausted",
    "restart:failed",
    "restart:recording",
    "timer:end",
    "timer:wait-existing-stt",
    "timeout-fallback:unavailable",
    "timeout-fallback:waiting-for-stt",
    "timeout-fallback:result",
    "timer:existing-stt-result",
    "timer:retry-word",
    "timer:go-next-word",
    "word-error:begin",
    "word-error:prompt-audio-failed",
    "word-error:prompt-repeat",
    "word-error:restart-failed-return-to-countdown",
    "go-next:begin",
    "go-next:restart-failed-return-to-countdown",
    "go-next:finish-run",
    "finish-run",
    "recording-attempt:set-active",
    "start-stt:error",
    "results:stale",
    "results:for-current-word",
    "technical-audio:retry",
    "technical-audio:restart-failed-return-to-countdown",
    "match:decision",
    "match:success-stop-recording",
    "match:no-success-wait-for-timer",
    "upload-result"
]);
const MEDIA_RECOVERY_AUTOMATIC_RETRY_LIMIT = 1;
const MEDIA_RECOVERY_RETRY_DELAY_SECONDS = 1.2;
const SILENT_RECOVERY_RETRY_LIMIT = 2;

interface RecordingAttemptContext {
    recordingAttemptId: string;
    flowName: string;
    intendedWord: string;
    tryNumber: number;
    acceptedWords: string[];
}

export const AssessmentPresentTargets = (): JSX.Element => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const appInsights = useAppInsightsContext();
    const finishRunInFlightRef = useRef(false);

    const assessment = useAppSelector((x) => x.assessment);
    const wordTime = useAppSelector((x) => x.globalSettings.presentTargets);

    const showTimer = useAppSelector((x) => x.globalSettings.showTimers);
    const sttDiagnosticsEnabled = useAppSelector((x) => x.globalSettings.enableSttDiagnostics);
    const wakeLockEnabled = useAppSelector((x) => x.globalSettings.enableAssessmentWakeLock);

    const [assessmentFlow] = useState(useAppSelector((x) => x.assessmentFlow));

    const currentFlow = assessmentFlow.flows[assessmentFlow.currentFlow];

    useScreenWakeLock({
        enabled: wakeLockEnabled,
        diagnosticsEnabled: sttDiagnosticsEnabled,
        area: PRESENT_TARGETS_DIAGNOSTIC_AREA,
        context: {
            flowName: currentFlow.name
        }
    });

    const { uploadAssessmentResponse } = useApi();

    const loadAudioForWord = (word: Word): void => {
        const newAudio = new Audio(word.audioInstructionUrl);
        newAudio.load();
        newAudio.loop = false;
        newAudio.volume = 0.6;

        setCurrentAudio(newAudio);
    };

    const [currentWord, setCurrentWord] = useState(assessment.wordLists[currentFlow.list].words[0]);
    const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement>();
    const [acceptedWords, setAcceptedWords] = useState(getAcceptedWords(currentWord));
    const [finished, setFinished] = useState(false);
    const [promptTitle, setPromptTitle] = useState<string>();

    const [isSecondTry, setIsSecondTry] = useState(false);
    const [hasSucceeded, setHasSucceded] = useState(false);
    const [resultWord, setResultWord] = useState<ResultType>();
    const [alternates, setAlternates] = useState<Word[]>([]);
    const [processingTimeoutResult, setProcessingTimeoutResult] = useState(false);
    const activeRecordingAttempt = useRef<RecordingAttemptContext>();
    const processingTimeoutResultRef = useRef(false);
    const waitingForExistingSttResultRef = useRef(false);
    const audioPipelineResetFlowsRef = useRef<Set<string>>(new Set());
    const mediaRecoveryFailureCountRef = useRef(0);
    const initialCountdownCompletedRef = useRef(false);
    const silentRecoveryAttemptCountRef = useRef(0);

    const [showCountdown, setShowCountDown] = useState(true);
    const [requiresUserMediaRecovery, setRequiresUserMediaRecovery] = useState(false);

    const logData = {
        id: uuidv4(),
        assessmentId: `present-target-${assessment.id}`,
        flow: "",
        word: "",
        part: 0,
        context: "",
        text: ""
    };

    const shortId = (value?: string): string | undefined => value?.slice(0, 8);

    const summarizeResult = (result?: ResultType): any => {
        if (!result) return undefined;

        return {
            w: result.intendedWord,
            tr: result.transcript,
            conf: result.confidence,
            src: result.source,
            el: result.elapsed,
            blob: result.speechBlob?.size,
            att: shortId(result.context?.recordingAttemptId),
            try: result.context?.tryNumber,
            fb: result.context?.timeoutFallback,
            technicalFailure: result.technicalFailure === true || result.context?.technicalAudioFailure === true
        };
    };

    const formatDebugValue = (value: any): string => {
        if (value === undefined) return "-";
        if (value === null) return "null";
        if (Array.isArray(value)) return value.join("|") || "-";
        if (typeof value === "object") return JSON.stringify(value);

        return `${value}`;
    };

    const debugLog = (event: string, data: any = {}): void => {
        if (!PRESENT_TARGETS_DIAGNOSTIC_EVENTS.has(event)) {
            return;
        }

        const logWord =
            data.forWord ?? data.intendedWord ?? data.nextWord ?? data.result?.intendedWord ?? currentWord.value;
        const attempt = data.recordingAttempt ?? data.activeAttempt ?? activeRecordingAttempt.current;
        const result = summarizeResult(data.result ?? data.resultWord ?? data.successResult ?? data.initialResult);
        const payload = {
            flow: currentFlow.name,
            w: logWord,
            cw: currentWord.value,
            att: shortId(attempt?.recordingAttemptId),
            attW: attempt?.intendedWord,
            try: data.tryNumber ?? attempt?.tryNumber ?? (isSecondTry ? 2 : 1),
            second: isSecondTry,
            ok: hasSucceeded,
            success: data.success,
            fb: processingTimeoutResult,
            proc: shortId(data.processingAttemptId ?? processingAttemptId),
            pTO: data.processAudioOnTimeout,
            upTry: data.uploadTryNumber,
            rsp: data.responseType,
            curCnt: data.currentWordResults?.length,
            gCnt: data.googleResults?.length,
            az: !!data.azureResults,
            acc: (data.acceptedWords ?? attempt?.acceptedWords)?.join("|"),
            tr: data.transcript?.join?.("|"),
            azTr: data.azureTranscript?.join?.("|"),
            hit: data.intersectTranscript?.join?.("|") ?? data.azureIntersectTranscript?.join?.("|"),
            conf: data.confidence,
            src: data.usedSource,
            staleCnt: data.incomingResults?.length,
            result: result
                ? `w=${formatDebugValue(result.w)},tr=${formatDebugValue(result.tr)},src=${formatDebugValue(
                      result.src
                  )},conf=${formatDebugValue(result.conf)},try=${formatDebugValue(result.try)},fb=${formatDebugValue(
                      result.fb
                  )},blob=${formatDebugValue(result.blob)},tech=${formatDebugValue(result.technicalFailure)}`
                : undefined
        };

        trackDiagnosticTrace({
            enabled: sttDiagnosticsEnabled,
            area: PRESENT_TARGETS_DIAGNOSTIC_AREA,
            event,
            properties: payload
        });
    };

    const createRecordingAttempt = (
        forWord: string,
        tryNumber: number,
        acceptedWordsForAttempt: string[]
    ): RecordingAttemptContext => {
        const attempt = {
            recordingAttemptId: uuidv4(),
            flowName: currentFlow.name,
            intendedWord: forWord,
            tryNumber,
            acceptedWords: acceptedWordsForAttempt
        };

        activeRecordingAttempt.current = attempt;
        debugLog("recording-attempt:set-active", attempt);

        return attempt;
    };

    const handleToggleCountdown = async (): Promise<void> => {
        initialCountdownCompletedRef.current = true;
        setRequiresUserMediaRecovery(false);
        logPresentTargetFlow("start handleToggleCountdown", { currentWordValue: currentWord.value });
        debugLog("countdown:done", { currentWordValue: currentWord.value });

        setShowCountDown(false);
        logPresentTargetFlow("trace handleToggleCountdown startSpeechToText");

        const shouldResetAudioPipelineBeforeFlow =
            currentFlow.name === "List B Present Targets 1" || currentFlow.name === "List B Present Targets 2";

        if (shouldResetAudioPipelineBeforeFlow && !audioPipelineResetFlowsRef.current.has(currentFlow.name)) {
            audioPipelineResetFlowsRef.current.add(currentFlow.name);
            const resetSucceeded = await hardResetAudioPipeline({
                flowName: currentFlow.name,
                preserveAudioContext: false,
                reason: "before-list-b-present-targets"
            });

            if (!resetSucceeded) {
                audioPipelineResetFlowsRef.current.delete(currentFlow.name);
                setShowCountDown(true);
                return;
            }
        }

        const started = await tryStartSpeechToText(currentWord.value);

        if (!started) {
            debugLog("start:failed-return-to-countdown", { currentWordValue: currentWord.value });
            setShowCountDown(true);
            return;
        }

        logPresentTargetFlow("trace handleToggleCountdown startSpeechToText");

        resetCounter();

        logPresentTargetFlow("end handleToggleCountdown");
    };

    const restartSTT = async (
        forWord: string,
        explicitContext?: string[],
        tryNumber = isSecondTry ? 2 : 1
    ): Promise<boolean> => {
        logPresentTargetFlow("start restartSTT", { forWord, explicitContext, tryNumber });
        debugLog("restart:begin", { forWord, explicitContext, tryNumber });

        logPresentTargetFlow("trace restartSTT startSpeechToText");

        const started = await tryStartSpeechToText(forWord, explicitContext, tryNumber);

        if (!started) {
            debugLog("restart:failed", { forWord, explicitContext, tryNumber });
            return false;
        }

        logPresentTargetFlow("trace restartSTT startSpeechToText");

        resetCounter();
        setHasSucceded(false);

        logPresentTargetFlow("end restartSTT");
        debugLog("restart:recording", { forWord, explicitContext, tryNumber });
        return true;
    };

    const handleOnTimerEnd = async (): Promise<void> => {
        logPresentTargetFlow("start handleOnTimerEnd");
        const processAudioOnTimeout = shouldProcessAudioOnTimeout();

        debugLog("timer:end", {
            processAudioOnTimeout,
            resultWord,
            acceptedWords
        });

        if (hasSucceeded || resultWord) {
            debugLog("timer:skip-stop-already-resolved", { resultWord });

            await handleUploadResult(hasSucceeded, resultWord);

            if ((!hasSucceeded && !isSecondTry) || !resultWord) {
                debugLog("timer:retry-word", { resultWord });
                await handleOnWordError();
            } else {
                debugLog("timer:go-next-word", { resultWord });
                await goToNextWord();
            }

            logPresentTargetFlow("end handleOnTimerEnd alreadyResolved");
            return;
        }

        if (processingAttemptId && processingAttemptId === activeRecordingAttempt.current?.recordingAttemptId) {
            waitingForExistingSttResultRef.current = true;
            debugLog("timer:wait-existing-stt", {
                processingAttemptId,
                activeAttempt: activeRecordingAttempt.current
            });
            logPresentTargetFlow("end handleOnTimerEnd waitingForExistingStt");
            return;
        }

        if (processAudioOnTimeout) {
            processingTimeoutResultRef.current = true;
            setProcessingTimeoutResult(true);
        }

        logPresentTargetFlow("trace handleOnTimerEnd stopSpeechToText");

        const audioProcessingStarted = await stopSpeechToText(processAudioOnTimeout, currentWord.value, acceptedWords, {
            ...activeRecordingAttempt.current,
            stepName: currentFlow.name,
            timeoutFallback: processAudioOnTimeout
        });

        logPresentTargetFlow("trace handleOnTimerEnd stopSpeechToText");

        if (processAudioOnTimeout) {
            if (!audioProcessingStarted) {
                debugLog("timeout-fallback:unavailable");
                processingTimeoutResultRef.current = false;
                setProcessingTimeoutResult(false);
                await handleUploadResult(false, undefined);

                if (!isSecondTry) {
                    await handleOnWordError();
                } else {
                    await goToNextWord();
                }

                logPresentTargetFlow("end handleOnTimerEnd fallbackUnavailable");
                return;
            }

            debugLog("timeout-fallback:waiting-for-stt");
            logPresentTargetFlow("end handleOnTimerEnd waitingForTimeoutFallback");
            return;
        }

        logPresentTargetFlow("trace handleOnTimerEnd handleUploadResult");

        void handleUploadResult(hasSucceeded, resultWord);

        logPresentTargetFlow("trace handleOnTimerEnd handleUploadResult");

        if ((!hasSucceeded && !isSecondTry) || !resultWord) {
            debugLog("timer:retry-word", { resultWord });
            void handleOnWordError();
        } else {
            debugLog("timer:go-next-word", { resultWord });
            void goToNextWord();
        }

        logPresentTargetFlow("end handleOnTimerEnd");
    };

    const handleOnWordError = async (): Promise<void> => {
        logPresentTargetFlow("start handleOnWordError");
        debugLog("word-error:begin", { resultWord });

        if (isSecondTry) {
            debugLog("word-error:second-try-go-next");
            void goToNextWord();
            return;
        }

        if (!resultWord) {
            setAlternates(alternates.concat(currentWord));
        }

        const titleForWord = translate("assessments.theWordIs", { word: currentWord.value });
        debugLog("word-error:prompt-repeat", { titleForWord });
        setIsSecondTry(true);
        setPromptTitle(titleForWord);
        setResultWord(undefined);
        setHasSucceded(false);
        processingTimeoutResultRef.current = false;
        waitingForExistingSttResultRef.current = false;
        setProcessingTimeoutResult(false);

        if (currentAudio) {
            const audioPlayed = await playAudioWithRetryAsync(() => new Audio(currentAudio.src));

            if (!audioPlayed) {
                debugLog("word-error:prompt-audio-failed", { titleForWord, audioUrl: currentAudio.src });
                setShowCountDown(true);
                return;
            }
        }

        const restarted = await restartSTT(currentWord.value, undefined, 2);

        if (!restarted) {
            debugLog("word-error:restart-failed-return-to-countdown");
            setShowCountDown(true);
        }

        logPresentTargetFlow("end handleOnWordError");
    };

    const goToNextWord = async (): Promise<void> => {
        logPresentTargetFlow("start goToNextWord");
        debugLog("go-next:begin");

        const next = getNextElement(currentWord, assessment.wordLists[currentFlow.list].words);

        logPresentTargetFlow("trace goToNextWord", next);

        if (!next) {
            debugLog("go-next:finish-run");
            logPresentTargetFlow("trace goToNextWord finishPresentTargetsRun");

            await finishPresentTargetsRun();

            logPresentTargetFlow("trace goToNextWord finishPresentTargetsRun");
            return;
        }

        setIsSecondTry(false);
        setPromptTitle("");
        loadAudioForWord(next);
        setCurrentWord(next);
        setAcceptedWords(getAcceptedWords(next));
        setResultWord(undefined);
        waitingForExistingSttResultRef.current = false;
        mediaRecoveryFailureCountRef.current = 0;
        setRequiresUserMediaRecovery(false);

        const restarted = await restartSTT(next.value, [next.value, ...next.alternates], 1);

        if (!restarted) {
            debugLog("go-next:restart-failed-return-to-countdown", { nextWord: next.value });
            setShowCountDown(true);
        }

        logPresentTargetFlow("end goToNextWord");
    };

    const {
        results,
        startSpeechToText,
        recordingVolume,
        stopSpeechToText,
        destroy,
        hardResetAudioPipeline,
        isRecording,
        isReady: isSpeechToTextReady,
        processingAttemptId
    } = useCustomSpeechToText({
        context: [currentWord.value, ...currentWord.alternates]
    });

    const tryStartSpeechToText = async (
        forWord: string,
        explicitContext?: string[],
        tryNumber = isSecondTry ? 2 : 1
    ): Promise<boolean> => {
        const acceptedWordsForAttempt = explicitContext ?? acceptedWords;
        const recordingAttempt = createRecordingAttempt(forWord, tryNumber, acceptedWordsForAttempt);
        const isAudioContextResumeError = (er: unknown): boolean =>
            er instanceof Error && er.message.includes("AudioContext could not be resumed");

        try {
            debugLog("start-stt:attempt", { forWord, explicitContext, recordingAttempt });
            await startSpeechToText(forWord, explicitContext, recordingAttempt);
            debugLog("start-stt:success", { forWord, explicitContext, recordingAttempt });
            mediaRecoveryFailureCountRef.current = 0;
            silentRecoveryAttemptCountRef.current = 0;
            setRequiresUserMediaRecovery(false);
            return true;
        } catch (er) {
            if (activeRecordingAttempt.current?.recordingAttemptId === recordingAttempt.recordingAttemptId) {
                activeRecordingAttempt.current = undefined;
            }

            debugLog("start-stt:error", { forWord, explicitContext, recordingAttempt, er });
            logPresentTargetFlow("error tryStartSpeechToText", {
                forWord,
                explicitContext,
                recordingAttempt,
                er
            });

            if (
                isAudioContextResumeError(er) &&
                mediaRecoveryFailureCountRef.current < MEDIA_RECOVERY_AUTOMATIC_RETRY_LIMIT
            ) {
                mediaRecoveryFailureCountRef.current = mediaRecoveryFailureCountRef.current + 1;
                debugLog("start:media-recovery-auto-reset-start", {
                    forWord,
                    explicitContext,
                    recordingAttempt,
                    failureCount: mediaRecoveryFailureCountRef.current
                });

                await sleepFor(MEDIA_RECOVERY_RETRY_DELAY_SECONDS);
                const resetSucceeded = await hardResetAudioPipeline({
                    flowName: currentFlow.name,
                    intendedWord: forWord,
                    tryNumber,
                    reason: "audio-context-resume-failed",
                    preserveAudioContext: false
                });

                debugLog("start:media-recovery-auto-reset-complete", {
                    forWord,
                    explicitContext,
                    recordingAttempt,
                    resetSucceeded,
                    failureCount: mediaRecoveryFailureCountRef.current
                });

                if (resetSucceeded) {
                    await sleepFor(MEDIA_RECOVERY_RETRY_DELAY_SECONDS);
                    activeRecordingAttempt.current = recordingAttempt;

                    try {
                        await startSpeechToText(forWord, explicitContext, recordingAttempt);
                        debugLog("start:media-recovery-retry-success", { forWord, explicitContext, recordingAttempt });
                        mediaRecoveryFailureCountRef.current = 0;
                        silentRecoveryAttemptCountRef.current = 0;
                        setRequiresUserMediaRecovery(false);
                        return true;
                    } catch (retryError) {
                        if (
                            activeRecordingAttempt.current?.recordingAttemptId === recordingAttempt.recordingAttemptId
                        ) {
                            activeRecordingAttempt.current = undefined;
                        }

                        debugLog("start:media-recovery-retry-failed", {
                            forWord,
                            explicitContext,
                            recordingAttempt,
                            retryError
                        });
                    }
                }
            }

            if (isAudioContextResumeError(er)) {
                debugLog("start:media-recovery-manual-required", {
                    forWord,
                    explicitContext,
                    recordingAttempt,
                    failureCount: mediaRecoveryFailureCountRef.current
                });
                setRequiresUserMediaRecovery(true);
            }

            return false;
        }
    };

    const shouldProcessAudioOnTimeout = (): boolean =>
        !hasSucceeded && !resultWord && !processingTimeoutResultRef.current;

    const [counter, resetCounter, cancelCounter] = useCountDown({
        from: wordTime,
        onDone: async () => {
            void handleOnTimerEnd();
        }
    });

    useEffect(() => {
        if (showCountdown) {
            cancelCounter();
        }
    }, [cancelCounter, showCountdown]);

    useEffect(() => {
        if (!showCountdown || !initialCountdownCompletedRef.current || !isSpeechToTextReady) {
            return;
        }

        if (silentRecoveryAttemptCountRef.current >= SILENT_RECOVERY_RETRY_LIMIT) {
            debugLog("silent-recovery:exhausted", {
                attempts: silentRecoveryAttemptCountRef.current,
                currentWordValue: currentWord.value
            });
            return;
        }

        silentRecoveryAttemptCountRef.current = silentRecoveryAttemptCountRef.current + 1;
        const recoveryTimer = window.setTimeout(() => {
            mediaRecoveryFailureCountRef.current = 0;
            void handleToggleCountdown();
        }, MEDIA_RECOVERY_RETRY_DELAY_SECONDS * 1000);

        return () => window.clearTimeout(recoveryTimer);
    }, [isSpeechToTextReady, requiresUserMediaRecovery, showCountdown]);

    const handleUploadResult = async (
        success: boolean,
        result?: ResultType,
        useTryNumber: boolean = true
    ): Promise<void> => {
        const resultTryNumber = result?.context?.tryNumber;
        const uploadTryNumber = useTryNumber ? resultTryNumber ?? (isSecondTry ? 2 : 1) : 0;

        logPresentTargetFlow("start handleUploadResult");
        debugLog("upload-result", {
            success,
            result,
            useTryNumber,
            uploadTryNumber,
            responseType: success ? WordResponseType.Correct : WordResponseType.Incorrect
        });

        try {
            logPresentTargetFlow("trace handleUploadResult uploadAssessmentResponse");

            void uploadAssessmentResponse(assessment.id ?? 0, {
                stepName: currentFlow.name,
                targetWord: currentWord.value,
                response: result?.transcript === "-" ? undefined : result?.transcript,
                responseType: success ? WordResponseType.Correct : WordResponseType.Incorrect,
                audio: result?.speechBlob,
                tryNumber: uploadTryNumber,
                source: result?.source
            });
        } catch (er) {
            logPresentTargetFlow("error handleUploadResult uploadAssessmentResponse", er);
            trackDiagnosticException({
                enabled: sttDiagnosticsEnabled,
                area: PRESENT_TARGETS_DIAGNOSTIC_AREA,
                event: "upload:response:error",
                error: er,
                properties: {
                    assessmentId: assessment.id,
                    flowName: currentFlow.name,
                    word: currentWord.value,
                    uploadTryNumber
                }
            });
        }

        logPresentTargetFlow("end handleUploadResult");
    };

    const handleTechnicalAudioRetry = async (
        activeAttempt: RecordingAttemptContext | undefined,
        matchAcceptedWords: string[],
        result: ResultType
    ): Promise<void> => {
        const retryNumber = activeAttempt?.tryNumber ?? (isSecondTry ? 2 : 1);

        debugLog("technical-audio:retry", {
            result,
            activeAttempt,
            tryNumber: retryNumber
        });

        setResultWord(undefined);
        setHasSucceded(false);
        processingTimeoutResultRef.current = false;
        waitingForExistingSttResultRef.current = false;
        setProcessingTimeoutResult(false);

        const restarted = await restartSTT(currentWord.value, matchAcceptedWords, retryNumber);

        if (!restarted) {
            debugLog("technical-audio:restart-failed-return-to-countdown", { result, activeAttempt });
            setShowCountDown(true);
        }
    };

    const handleResultForWord = async (incomingResults: ResultType[]): Promise<void> => {
        logPresentTargetFlow("start handleResultForWord");
        debugLog("results:incoming", { incomingResults });

        const activeAttempt = activeRecordingAttempt.current;
        const currentWordResults = incomingResults.filter(
            (x) =>
                x.intendedWord === currentWord.value &&
                x.context?.recordingAttemptId === activeAttempt?.recordingAttemptId
        );

        if (!currentWordResults.length) {
            debugLog("results:stale", { incomingResults, activeAttempt });
            logPresentTargetFlow("end handleResultForWord staleResults");
            return;
        }

        const googleResults = currentWordResults.filter((x) => x.source === SttSourceType.Google);
        const azureResults = currentWordResults.find((x) => x.source === SttSourceType.Azure);
        const technicalAudioFailure = currentWordResults.find(
            (x) => x.technicalFailure === true || x.context?.technicalAudioFailure === true
        );

        const highestConfidenceResult = googleResults.sort((x, y) => y.confidence - x.confidence)[0];
        const initialResult = highestConfidenceResult ?? azureResults ?? currentWordResults[0];
        const matchAcceptedWords = activeAttempt?.acceptedWords ?? acceptedWords;
        const isTimeoutFallbackResult =
            processingTimeoutResultRef.current || currentWordResults.some((x) => x.context?.timeoutFallback);
        const isExistingSttTimerResult = waitingForExistingSttResultRef.current;

        logPresentTargetFlow("trace handleResultForWord", initialResult);
        debugLog("results:for-current-word", {
            currentWordResults,
            googleResults,
            azureResults,
            initialResult,
            acceptedWords: matchAcceptedWords,
            activeAttempt,
            isTimeoutFallbackResult,
            isExistingSttTimerResult
        });

        if (technicalAudioFailure) {
            await handleTechnicalAudioRetry(activeAttempt, matchAcceptedWords, technicalAudioFailure);

            logPresentTargetFlow("end handleResultForWord technicalAudioRetry");
            return;
        }

        let success = false;
        let successResult: ResultType = initialResult;

        for (const result of googleResults) {
            const transcript = sanitizeResponse(result.transcript).split(" ");
            const intersectTranscript = intersection(matchAcceptedWords, transcript);

            debugLog("match:google", {
                transcript,
                intersectTranscript,
                confidence: result.confidence
            });

            if (intersectTranscript.length > 0) {
                success = true;
                successResult = result;
                successResult.transcript = intersectTranscript[0];
                break;
            }
        }

        const azureTranscript = sanitizeResponse(azureResults?.transcript?.toLocaleLowerCase() ?? "").split(" ");
        const azureIntersectTranscript = intersection(matchAcceptedWords, azureTranscript);

        debugLog("match:azure", {
            azureTranscript,
            azureIntersectTranscript,
            confidence: azureResults?.confidence
        });

        // if google is success
        if (success) {
            // check if azure has match if not save alternate
            if (azureIntersectTranscript.length === 0 && azureResults?.transcript) {
                void handleUploadResult(false, azureResults, false);
            }
        } else {
            // set azure response if success
            if (azureIntersectTranscript.length > 0) {
                // save google response if contains alternate
                if (successResult?.transcript && successResult?.transcript !== "-") {
                    void handleUploadResult(false, successResult, false);
                }

                success = true;
                successResult = azureResults!;
                successResult.transcript = azureIntersectTranscript[0];
            } else {
                if (azureResults?.transcript) {
                    void handleUploadResult(false, azureResults, false);
                }
            }
        }

        setResultWord(successResult);
        debugLog("match:decision", {
            success,
            successResult,
            usedSource: successResult?.source
        });

        if (isTimeoutFallbackResult || isExistingSttTimerResult) {
            debugLog(isTimeoutFallbackResult ? "timeout-fallback:result" : "timer:existing-stt-result", {
                success,
                successResult
            });
            processingTimeoutResultRef.current = false;
            waitingForExistingSttResultRef.current = false;
            setProcessingTimeoutResult(false);

            if (success && isExistingSttTimerResult) {
                logPresentTargetFlow("start handleResultForWord stopSpeechToText existingSttTimerResult");
                await stopSpeechToText(false, currentWord.value, matchAcceptedWords, activeAttempt);
                logPresentTargetFlow("end handleResultForWord stopSpeechToText existingSttTimerResult");
            }

            await handleUploadResult(success, successResult);

            if (!success && !isSecondTry) {
                debugLog(isTimeoutFallbackResult ? "timeout-fallback:retry-word" : "timer:existing-stt-retry-word");
                await handleOnWordError();
            } else {
                debugLog(isTimeoutFallbackResult ? "timeout-fallback:go-next-word" : "timer:existing-stt-go-next-word");
                await goToNextWord();
            }

            logPresentTargetFlow("end handleResultForWord timeoutFallback");
            return;
        }

        if (success) {
            setHasSucceded(true);
            debugLog("match:success-stop-recording");

            logPresentTargetFlow("start handleResultForWord stopSpeechToText");

            await stopSpeechToText(false, currentWord.value, matchAcceptedWords, activeAttempt);

            logPresentTargetFlow("start handleResultForWord stopSpeechToText");
        } else {
            setHasSucceded(false);
            debugLog("match:no-success-wait-for-timer");
        }

        logPresentTargetFlow("end handleResultForWord");
    };

    const finishPresentTargetsRun = async (): Promise<void> => {
        if (finishRunInFlightRef.current) {
            logPresentTargetFlow("skip finishPresentTargetsRun already in flight");
            return;
        }

        finishRunInFlightRef.current = true;
        logPresentTargetFlow("start finishPresentTargetsRun");
        debugLog("finish-run");

        setFinished(true);
        cancelCounter();
        setPromptTitle("");

        destroy();
        if (!assessmentFlow.flows[assessmentFlow.currentFlow + 1]) {
            navigate(NavItems.Assessments.AssessmentClosingVideo.path);
            return;
        }

        navigate(assessmentFlow.flows[assessmentFlow.currentFlow + 1].path);

        logPresentTargetFlow("end finishPresentTargetsRun");
    };

    useEffect(() => {
        logPresentTargetFlow("start useEffect currentWord");

        setAcceptedWords(getAcceptedWords(currentWord));

        logPresentTargetFlow("end useEffect currentWord");
    }, [currentWord]);

    useEffect(() => {
        logPresentTargetFlow("start useEffect results", results?.length);

        if (!results?.length) {
            return;
        }

        void handleResultForWord(results);

        logPresentTargetFlow("end useEffect results");
    }, [results]);

    useEffect(() => {
        logPresentTargetFlow("start useEffect");

        loadAudioForWord(currentWord);
        dispatch(updateCurrentFlow());

        logPresentTargetFlow("end useEffect");
    }, []);

    const progress = Math.floor(((wordTime - counter) * 100) / 6);
    const time = new Date((wordTime - (wordTime - counter)) * 1000).toISOString().slice(14, 19);
    const showInitialCountdown = showCountdown && !initialCountdownCompletedRef.current;

    const logPresentTargetFlow = (text: string, context: any = {}): void => {
        if (!sttDiagnosticsEnabled || !text.startsWith("error")) {
            return;
        }

        logData.part = logData.part + 1;
        logData.flow = currentFlow.name;
        logData.word = currentWord.value;
        logData.text = text;
        logData.context = JSON.stringify(context);

        appInsights.trackTrace({
            message: JSON.stringify(logData),
            severityLevel: SeverityLevel.Information
        });
    };

    return (
        <AssessmentShell micVolume={recordingVolume} micIsRecording={isRecording} navBarTitle={promptTitle}>
            {showInitialCountdown ? (
                !isSpeechToTextReady ? (
                    <div className="assessment-stage" style={{ paddingBottom: "6rem" }}>
                        <Loader />
                    </div>
                ) : (
                    <div className="assessment-stage" style={{ paddingBottom: "6rem" }}>
                        <LCountDown from={3} onDone={handleToggleCountdown} />
                    </div>
                )
            ) : (
                <div className="assessment-stage is-flex is-flex-direction-column" style={{ paddingBottom: "6rem" }}>
                    {showTimer && (
                        <div className="assessment-timer" style={{ position: "absolute", right: "8%", top: "2%" }}>
                            <LProgressCircle
                                progress={progress}
                                text={time}
                                labelColor={Colors.primaryBlue}
                                trackColor={Colors.primaryBlue}
                                indicatorColor={Colors.primaryBlue}
                            />
                            <div>{results[0]?.transcript}</div>
                        </div>
                    )}
                    {!finished ? (
                        <LTitle overrideSize="5rem" color="has-text-dark-blue" className="assessment-stimulus mb-6">
                            {currentWord.value}
                        </LTitle>
                    ) : (
                        <FontAwesomeIcon icon={solid("circle-check")} size="6x" color={Colors.successGreen} />
                    )}
                </div>
            )}
            <AssessmentTimeout
                activityKey={`${currentFlow.name}:${currentWord.value}:${isRecording}:${processingAttemptId ?? ""}`}
                micVolume={recordingVolume}
            />
        </AssessmentShell>
    );
};
